import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderIcon, PlusIcon, ChartBarIcon, UsersIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import useProjectStore from "../../store/projectStore";
import useAuthStore from "../../store/authStore";
import { bladesApi } from "../../api/blades";
import { projectsApi } from "../../api/projects";
import { useLookups } from "../../hooks/useLookups";

import { useChartColors } from "../../chartTheme";

// ── Small building blocks ─────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, accent }) {
  const { COLOR_PRIMARY } = useChartColors();
  const color = accent ?? COLOR_PRIMARY;
  return (
    <Card>
      <CardBody className="flex items-start gap-3 py-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: color + "18" }}>
          <Icon className="h-4.5 w-4.5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-gray-900 leading-tight">
            {value ?? "—"}
          </p>
          <p className="text-xs text-gray-500 leading-tight">{label}</p>
          {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
      </CardBody>
    </Card>
  );
}

function DistRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.max(2, Math.round((count / total) * 100)) : 0;
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span className="text-xs text-gray-600 w-40 truncate shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-5 text-right shrink-0">{count}</span>
    </div>
  );
}

function DistCard({ title, data, resolveLabel }) {
  const { BAR_PALETTE } = useChartColors();
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <Card>
      <CardBody className="py-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
        {entries.length === 0 ? (
          <p className="text-xs text-gray-400">No data yet</p>
        ) : (
          entries.map(([key, count], i) => {
            const label = resolveLabel ? resolveLabel(parseInt(key)) : key;
            return (
              <DistRow key={key} label={label} count={count} total={total}
                       color={BAR_PALETTE[i % BAR_PALETTE.length]} />
            );
          })
        )}
      </CardBody>
    </Card>
  );
}

function SectionHeading({ icon: Icon, children }) {
  return (
    <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </h2>
  );
}

function ProjectRow({ project, onClick, colorFor, labelFor, showEntity }) {
  return (
    <Card className="cursor-pointer hover:border-bh-300 transition-colors" onClick={onClick}>
      <CardBody className="py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Badge color={colorFor(project.machine_type_id)}>
              {labelFor(project.machine_type_id)}
            </Badge>
            <span className="font-medium text-gray-900 text-sm truncate">{project.name}</span>
            {showEntity && project.entity && (
              <span className="text-[10px] bg-bh-50 text-bh-700 border border-bh-200 rounded px-1.5 py-0.5 shrink-0">
                {project.entity}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400 shrink-0">
            {new Date(project.updated_at).toLocaleDateString()}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

function DesignRow({ design, projectName, onClick, labelForMeco, showEntity }) {
  const mecoLabel = labelForMeco(design.meco_type_id);
  return (
    <Card className="cursor-pointer hover:border-bh-300 transition-colors" onClick={onClick}>
      <CardBody className="py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-gray-900 text-sm truncate">{design.name}</span>
              <span className="text-xs text-gray-400">v{design.version}</span>
              {design.meco_type_id && mecoLabel !== "—" && (
                <span className="text-[10px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">
                  {mecoLabel}
                </span>
              )}
              {design.is_released && <Badge color="yellow">Released</Badge>}
              {showEntity && design.entity && (
                <span className="text-[10px] bg-bh-50 text-bh-700 border border-bh-200 rounded px-1.5 py-0.5">
                  {design.entity}
                </span>
              )}
            </div>
            {projectName && (
              <p className="text-[11px] text-gray-400 mt-0.5">{projectName}</p>
            )}
          </div>
          <span className="text-xs text-gray-400 shrink-0">
            {new Date(design.updated_at).toLocaleDateString()}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { COLOR_DARK, COLOR_PRIMARY } = useChartColors();
  const { user } = useAuthStore();
  const { projects, fetchProjects, loading } = useProjectStore();
  const navigate = useNavigate();
  const { colorFor, labelFor } = useLookups("machine_type");
  const { labelFor: labelForMeco } = useLookups("meco_type");

  const [recentDesigns, setRecentDesigns] = useState([]);
  const [stats, setStats]                 = useState(null);

  useEffect(() => {
    fetchProjects();
    bladesApi.list().then(({ data }) => setRecentDesigns(data.slice(0, 8)));
    projectsApi.stats().then(({ data }) => setStats(data)).catch(() => {});
  }, [fetchProjects]);

  const pinnedIds      = new Set(user?.pinned_project_ids || []);
  const pinnedProjects = projects.filter(p => pinnedIds.has(p.id));
  const recentProjects = projects.slice(0, 5);
  const projectMap     = Object.fromEntries(projects.map(p => [p.id, p]));

  return (
    <>
      <TopBar title="Dashboard" actions={
        <Button icon={PlusIcon} onClick={() => navigate("/projects")}>New Project</Button>
      } />
      <main className="flex-1 overflow-y-auto p-6 space-y-6">

        <p className="text-gray-500 -mt-2">
          Welcome back, <span className="font-medium text-gray-800">{user?.full_name || user?.username}</span>
        </p>

        {/* ── Stat cards ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Total Projects"  value={stats?.total_projects}
                    icon={FolderIcon}   accent="#3b82f6" />
          <StatCard label="Total Designs"   value={stats?.total_designs}
                    icon={ChartBarIcon} accent="#8b5cf6" />
          <StatCard label="My Projects"     value={stats?.my_projects}
                    sub="owned by you"
                    icon={FolderIcon}   accent={COLOR_DARK} />
          <StatCard label="My Designs"      value={stats?.my_designs}
                    sub="created or edited by you"
                    icon={UsersIcon}    accent={COLOR_PRIMARY} />
        </div>

        {/* ── Distribution cards ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <DistCard title="Projects by type"      data={stats?.projects_by_type}     resolveLabel={labelFor} />
          <DistCard title="Designs by MECO type"  data={stats?.designs_by_meco_type} resolveLabel={labelForMeco} />
        </div>

        {/* ── Lists ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-6">

          {/* Left: pinned + recent projects */}
          <div className="space-y-5">

            {pinnedProjects.length > 0 && (
              <section>
                <SectionHeading icon={() => <StarIcon className="h-3.5 w-3.5 text-amber-400" />}>
                  Pinned Projects
                </SectionHeading>
                <div className="space-y-2">
                  {pinnedProjects.map(p => (
                    <ProjectRow key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} colorFor={colorFor} labelFor={labelFor} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <SectionHeading icon={FolderIcon}>Recent Projects</SectionHeading>
              {loading ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : recentProjects.length === 0 ? (
                <Card>
                  <CardBody className="text-center py-10">
                    <FolderIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-3">No projects yet.</p>
                    <Button onClick={() => navigate("/projects")} icon={PlusIcon} size="sm">
                      Create first project
                    </Button>
                  </CardBody>
                </Card>
              ) : (
                <div className="space-y-2">
                  {recentProjects.map(p => (
                    <ProjectRow key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} colorFor={colorFor} labelFor={labelFor} showEntity={user?.is_admin} />
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Right: recent designs */}
          <section>
            <SectionHeading icon={ChartBarIcon}>Recent Designs</SectionHeading>
            {recentDesigns.length === 0 ? (
              <Card>
                <CardBody className="text-center py-10">
                  <ChartBarIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No designs yet.</p>
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-2">
                {recentDesigns.map(d => (
                  <DesignRow
                    key={d.id}
                    design={d}
                    projectName={projectMap[d.project_id]?.name ?? d.project_name}
                    onClick={() => navigate(`/projects/${d.project_id}/blade/${d.id}/summary`)}
                    labelForMeco={labelForMeco}
                    showEntity={user?.is_admin}
                  />
                ))}
              </div>
            )}
          </section>

        </div>
      </main>
    </>
  );
}
